import { useState, useCallback, useRef, useEffect } from 'react';
import { makeApiCall } from '../utils/api';
import { API_PATHS } from '../config/api-endpoints';

interface UseFeedbackGenerationProps {
    jobId: string;
    onSuccess?: (jobData: any) => void;
    onError?: (error: string) => void;
}

export const useFeedbackGeneration = ({ 
    jobId, 
    onSuccess, 
    onError 
}: UseFeedbackGenerationProps) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'polling' | 'success' | 'failed'>('idle');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [generationProgress, setGenerationProgress] = useState(0);
    
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number | null>(null);

    // Calculate word count
    const handleFeedbackChange = useCallback((text: string) => {
        setFeedbackText(text);
        const words = text.trim().split(/\s+/).filter(word => word.length > 0);
        setWordCount(words.length);
    }, []);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, []);

    // Start progress tracking (assumes ~5 min for code generation)
    const startProgressTracking = useCallback(() => {
        startTimeRef.current = Date.now();
        setGenerationProgress(0);
        
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        
        progressIntervalRef.current = setInterval(() => {
            if (startTimeRef.current) {
                const elapsed = Date.now() - startTimeRef.current;
                const expectedDuration = 300000; // 5 minutes
                const progress = Math.min(90, Math.floor((elapsed / expectedDuration) * 90));
                setGenerationProgress(progress);
            }
        }, 1000);
    }, []);
    
    const stopProgressTracking = useCallback(() => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
        startTimeRef.current = null;
    }, []);

    const clearPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    }, []);

    // Poll job status via GetJob, checking generateStatus
    const pollJobStatus = useCallback(async (targetJobId: string) => {
        try {
            const response = await makeApiCall(
                API_PATHS.JOBS.GET,
                'POST',
                { jobId: targetJobId }
            );

            const job = response?.job;
            if (!job) return;

            const genStatus = job.generateStatus;
            
            if (genStatus === 'COMPLETED') {
                setGenerationStatus('success');
                setIsGenerating(false);
                setSuccessMessage('Code regenerated successfully with your feedback!');
                setGenerationProgress(100);
                clearPolling();
                stopProgressTracking();
                setFeedbackText('');
                setWordCount(0);
                if (onSuccess) onSuccess(job);
            } else if (genStatus === 'FAILED') {
                setGenerationStatus('failed');
                setIsGenerating(false);
                const detailedError = job.generateError || 'Code regeneration failed. Please review your feedback and try again.';
                setError(detailedError);
                setGenerationProgress(0);
                clearPolling();
                stopProgressTracking();
                if (onError) onError(detailedError);
            }
            // If SUBMITTED or GENERATING, continue polling
        } catch (err: any) {
            console.error('Error polling job status:', err);
            // Don't stop polling on transient errors
        }
    }, [onSuccess, onError, stopProgressTracking, clearPolling]);

    // Start polling with initial delay
    const startPolling = useCallback((targetJobId: string) => {
        setGenerationStatus('polling');
        
        // Initial delay of 15 seconds before first poll
        pollTimeoutRef.current = setTimeout(() => {
            pollJobStatus(targetJobId);
            
            // Then poll every 15 seconds
            pollIntervalRef.current = setInterval(() => {
                pollJobStatus(targetJobId);
            }, 15000);
        }, 15000);
    }, [pollJobStatus]);

    // Submit feedback via RegenerateCode
    const submitFeedback = useCallback(async () => {
        if (!feedbackText.trim() || wordCount > 300 || isGenerating) return;

        setIsGenerating(true);
        setGenerationStatus('generating');
        setError(null);
        setSuccessMessage(null);
        startProgressTracking();

        try {
            const response = await makeApiCall(
                API_PATHS.CODE_GENERATION.REGENERATE,
                'POST',
                {
                    jobId: jobId,
                    feedback: feedbackText.trim()
                }
            );

            const targetJobId = response.jobId || jobId;
            if (targetJobId) {
                startPolling(targetJobId);
            } else {
                throw new Error('No job ID available for polling');
            }
        } catch (err: any) {
            console.error('Failed to submit feedback:', err);
            setError(err.message || 'Failed to submit feedback. Please try again.');
            setIsGenerating(false);
            setGenerationStatus('failed');
            setGenerationProgress(0);
            stopProgressTracking();
            if (onError) onError(err.message || 'Failed to submit feedback');
        }
    }, [feedbackText, wordCount, isGenerating, jobId, startPolling, onError, startProgressTracking, stopProgressTracking]);

    const clearError = useCallback(() => setError(null), []);
    const clearSuccessMessage = useCallback(() => setSuccessMessage(null), []);

    const reset = useCallback(() => {
        setFeedbackText('');
        setWordCount(0);
        setIsGenerating(false);
        setGenerationStatus('idle');
        setError(null);
        setSuccessMessage(null);
        setGenerationProgress(0);
        clearPolling();
        stopProgressTracking();
    }, [clearPolling, stopProgressTracking]);

    return {
        feedbackText,
        setFeedbackText: handleFeedbackChange,
        wordCount,
        isGenerating,
        generationStatus,
        generationProgress,
        submitFeedback,
        error,
        clearError,
        successMessage,
        clearSuccessMessage,
        reset
    };
};
